import {useQuery} from "@tanstack/react-query";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getPreferences} from "@/services/preferenceService.ts";
import {ToolbarOptions} from "@/types/data-table/ToolbarOptions.ts";
import {useMemo} from "react";
import {getPreferencesColumns} from "@/pages/dashboard/admin/preferences-columns.tsx";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {DataTable} from "@/components/data-table/data-table.tsx";
import {getItemsOfAllTypes} from "@/services/itemService.ts";
import {useTranslation} from "react-i18next";
import {isPreferenceSubmissionPeriod} from "@/utils/time-helper.ts";

type PreferencePageProps = {
    submitPreferenceDeadline: Date | undefined;
    seasonId?: number;
}

export const PreferencesPage = ({submitPreferenceDeadline, seasonId}: PreferencePageProps) => {
    const {t} = useTranslation();

    const {data: preferences, isFetching: fetchingPreference} = useQuery({
        queryKey: [...queryKeys.fetchPreferences.all, seasonId ?? "active"],
        queryFn: () => getPreferences(seasonId),
        initialData: [],
    });

    const {data: criteria, isFetching: fetchingCriteria} = useQuery({
        queryKey: queryKeys.fetchCriteria.all,
        queryFn: () => getItemsOfAllTypes(),
    });

    const toolbarOptions: ToolbarOptions = {
        sendEmailColumn: "email",
        filters: [
            {
                column: "languages",
                title: "languages"
            },
            {
                column: "broad_criteria_1",
                title: "broad_criteria_1"
            },
            {
                column: "broad_criteria_2",
                title: "broad_criteria_2"
            },
            {
                column: "broad_criteria_3",
                title: "broad_criteria_3"
            },
            {
                column: "specific_criteria_1",
                title: "specific_criteria_1"
            },
            {
                column: "specific_criteria_2",
                title: "specific_criteria_2"
            },
            {
                column: "specific_criteria_3",
                title: "specific_criteria_3"
            },
            {
                column: "specific_criteria_4",
                title: "specific_criteria_4"
            },
            {
                column: "affinity_groups",
                title: "affinity_groups"
            },
        ],
        searchableColumn: "name"
    }

    const hiddenColumns: string[] = ["specific_criteria_4", "matched"];

    if (!isPreferenceSubmissionPeriod(submitPreferenceDeadline)) {
        toolbarOptions.filters.unshift({
            column: "matched",
            title: "matched"
        });
    }

    const columns = useMemo(() => {
        if (!criteria) return [];
        return getPreferencesColumns({criteria, t});
    }, [criteria, t]);

    return (
        <Card className="border-0">
            <CardHeader>
                <div className="flex justify-between">
                    <div>
                        <CardTitle>{t("preference.all")}</CardTitle>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {(fetchingPreference || fetchingCriteria) && <span>{t("common.loading")}</span>}
                {!fetchingPreference && !fetchingCriteria &&
                    <DataTable data={preferences} columns={columns} hiddenColumns={hiddenColumns} id="preferences"
                               toolbarOptions={toolbarOptions}/>}
            </CardContent>
        </Card>
    );
}
